import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { EyeIcon, EyeOffIcon, Info, ShieldCheck } from "lucide-react";
import { Button, Spinner } from "@components";
import { AuthenticationService } from "@authentication/AuthenticationService";
import {
  validatePassword,
  calculatePasswordStrength,
  getPasswordStrengthColor,
} from "@utils/validators";
import PasswordRequirements from "../resetPassword/components/PasswordRequirements";

/**
 * ChangePasswordForm
 * ------------------
 * Form for a logged-in user to change their own password. Posts to
 * /auth/distributor-user/change-password. Adapted from the same flow in
 * valyrion-web-app (UpdatePassword) but using this project's components,
 * validators, and AuthenticationService.changePassword().
 */

interface ChangePasswordFormProps {
  onClose: () => void;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onClose }) => {
  /********** STATE **********/
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);

  const newPasswordInputRef = useRef<HTMLInputElement>(null);
  const authService = new AuthenticationService();

  /********** EFFECTS **********/
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(newPassword));
  }, [newPassword]);

  /********** HANDLERS **********/
  const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(e.target.value);
    setCurrentPasswordError(e.target.value ? null : "Current password is required");
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);

    if (!value) {
      setNewPasswordError("New password is required");
    } else if (!validatePassword(value)) {
      setNewPasswordError(
        "Password must be at least 8 characters and include uppercase, lowercase, and a number."
      );
    } else {
      setNewPasswordError(null);
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setConfirmPasswordError(value === newPassword ? null : "Passwords do not match");
  };

  const handleUpdatePassword = async () => {
    if (!currentPassword.trim()) {
      setCurrentPasswordError("Current password is required");
      return;
    }
    if (!validatePassword(newPassword)) {
      setNewPasswordError(
        "Password must be at least 8 characters and include uppercase, lowercase, and a number"
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      setUpdateSuccess(true);
      toast.success("Password updated successfully");
    } catch (error: any) {
      console.error("Password update failed:", error);
      const message = error?.response?.data?.message || "Password update failed";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  /********** RENDER **********/
  if (updateSuccess) {
    return (
      <div className="w-[420px] text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-green-100 rounded-full p-3">
            <ShieldCheck size={40} className="text-green-600" />
          </div>
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Password Updated</h3>
        <p className="text-gray-600 mb-6">Your password has been successfully updated.</p>
        <Button variant="primary" className="w-full" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleUpdatePassword();
      }}
      className="w-[420px] space-y-4"
    >
      {/* Current Password */}
      <div>
        <label htmlFor="currentPassword" className="text-sm font-medium text-gray-700 mb-1 block">
          Current Password
        </label>
        <div className="relative">
          <input
            type={showCurrentPassword ? "text" : "password"}
            id="currentPassword"
            placeholder="Enter your current password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={handleCurrentPasswordChange}
            aria-invalid={!!currentPasswordError}
            className={`border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black w-full pr-12 ${
              currentPasswordError ? "border-red-500 focus:ring-red-500" : ""
            }`}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-gray-700"
            onClick={() => setShowCurrentPassword((v) => !v)}
            aria-label={showCurrentPassword ? "Hide current password" : "Show current password"}
          >
            {showCurrentPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </button>
        </div>
        {currentPasswordError && (
          <small className="mt-1 text-xs text-red-500 block">{currentPasswordError}</small>
        )}
      </div>

      {/* New Password */}
      <div>
        <label htmlFor="newPassword" className="text-sm font-medium text-gray-700 mb-1 flex items-center">
          New Password
          <Info
            size={13}
            className="ml-1 cursor-help"
            onMouseEnter={() => setShowRequirements(true)}
            onMouseLeave={() => setShowRequirements(false)}
          />
        </label>
        <div className="relative">
          <input
            ref={newPasswordInputRef}
            type={showNewPassword ? "text" : "password"}
            id="newPassword"
            placeholder="Enter your new password"
            autoComplete="new-password"
            value={newPassword}
            onChange={handleNewPasswordChange}
            aria-invalid={!!newPasswordError}
            className={`border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black w-full pr-12 ${
              newPasswordError ? "border-red-500 focus:ring-red-500" : ""
            }`}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-gray-700"
            onClick={() => setShowNewPassword((v) => !v)}
            aria-label={showNewPassword ? "Hide password" : "Show password"}
          >
            {showNewPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </button>
        </div>

        {/* Strength indicator */}
        {newPassword && (
          <div className="mt-2">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ease-out ${getPasswordStrengthColor(passwordStrength)}`}
                style={{ width: `${passwordStrength}%` }}
              />
            </div>
          </div>
        )}

        {/* Requirements tooltip */}
        <div className="relative">
          <div
            className={`absolute top-2 left-0 z-10 shadow-lg rounded-lg w-64 transition-opacity duration-150 ${
              showRequirements ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <PasswordRequirements password={newPassword} passwordStrength={passwordStrength} />
          </div>
        </div>

        {newPasswordError && (
          <small className="mt-1 text-xs text-red-500 block">{newPasswordError}</small>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700 mb-1 block">
          Confirm New Password
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            placeholder="Confirm your new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            aria-invalid={!!confirmPasswordError}
            className={`border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black w-full pr-12 ${
              confirmPasswordError ? "border-red-500 focus:ring-red-500" : ""
            }`}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-gray-700"
            onClick={() => setShowConfirmPassword((v) => !v)}
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            {showConfirmPassword ? <EyeOffIcon size={16} /> : <EyeIcon size={16} />}
          </button>
        </div>
        {confirmPasswordError && (
          <small className="mt-1 text-xs text-red-500 block">{confirmPasswordError}</small>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 mt-2">
        <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={
            !!currentPasswordError ||
            !!newPasswordError ||
            !!confirmPasswordError ||
            !currentPassword.trim() ||
            !newPassword ||
            !confirmPassword ||
            isLoading
          }
        >
          {isLoading && <Spinner className="mr-2" />}
          {isLoading ? "Updating..." : "Update Password"}
        </Button>
      </div>
    </form>
  );
};
